import { type AnchorHTMLAttributes, forwardRef, type Ref } from 'react';
import { graphql } from 'gatsby';
import { StyledLink, StyledIcon, type LinkVariant } from './Link.styles';
import useLinkProps from './hooks/useLinkProps';

export interface LinkProps
  extends Queries.LINKFragment,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'ref'> {
  className?: string;
  variant?: LinkVariant;
  queryParams?: Record<string, string>;
  isLoading?: boolean;
  disabled?: boolean;
}

const Link = forwardRef(
  (
    { variant, linkElements, children, queryParams, onClick, isLoading, ...rest }: LinkProps,
    ref?: Ref<HTMLAnchorElement>
  ) => {
    const { action, target, text, icon, event, cmsVariant } = linkElements;
    const hasIcon = Boolean(icon?.value?.url);

    // Variant selected from Kontent takes precedence over the one passed as prop
    const selectedVariant = cmsVariant?.value ?? variant;

    const { linkChildren, linkProps } = useLinkProps({
      action: action.value,
      target: target?.value,
      event: event?.value,
      queryParams,
      onClick,
    });

    return (
      <>
        <StyledLink {...linkProps} {...rest} ref={ref} variant={selectedVariant}>
          {text.value}
          {children}
          {hasIcon && !isLoading && <StyledIcon {...icon!.value!} />}
        </StyledLink>
        {linkChildren}
      </>
    );
  }
);

export const fragment = graphql`
  fragment LINK on kontent_item_link {
    system {
      codename
      id
    }
    __typename
    linkElements: elements {
      text {
        value
      }
      icon {
        value {
          url
        }
      }
      action {
        value {
          ...LINK_ACTION_OPEN_MODAL
          ...LINK_ACTION_OPEN_MODAL_V2
          ...LINK_ACTION_INTERNAL_PAGE
          ...LINK_ACTION_EXTERNAL_PAGE
          ...LINK_ACTION_ASSET
          ...LINK_ACTION_NAVATTIC_POP_UP
          ...LINK_ACTION_OPTANON_POP_UP
          ...LINK_ACTION_ACCESSIBILITY_WIDGET
        }
      }
      target {
        value
      }
      event {
        value {
          ...DATALAYER_EVENT
        }
      }
      cmsVariant: variant {
        value
      }
    }
  }

  fragment LINK_ACTION_OPEN_MODAL on kontent_item_link_action_open_modal {
    __typename
    elements {
      modal {
        value {
          ...MODAL
        }
      }
    }
  }

  fragment LINK_ACTION_OPEN_MODAL_V2 on kontent_item_link_action_open_modal_v2 {
    __typename
    elements {
      modal {
        value {
          system {
            id
          }
          preferred_language
        }
      }
    }
  }

  fragment LINK_ACTION_INTERNAL_PAGE on kontent_item_link_action_internal_page {
    __typename
    elements {
      page {
        value {
          ... on kontent_item {
            system {
              codename
            }
          }
        }
      }
      building_block_wrapper {
        value {
          ... on kontent_item_building_block_wrapper {
            elements {
              anchor_id {
                value
              }
            }
          }
        }
      }
    }
  }

  fragment LINK_ACTION_EXTERNAL_PAGE on kontent_item_link_action_external_page {
    __typename
    elements {
      url {
        value
      }
    }
  }

  fragment LINK_ACTION_ASSET on kontent_item_link_action_asset {
    __typename
    elements {
      asset {
        value {
          url
          name
        }
      }
      force_download {
        value
      }
      redirect_url {
        value
      }
    }
  }

  fragment LINK_ACTION_NAVATTIC_POP_UP on kontent_item_link_action_navattic_pop_up {
    __typename
    elements {
      url {
        value
      }
      title {
        value
      }
    }
  }
  fragment LINK_ACTION_OPTANON_POP_UP on kontent_item_link_action_optanon_pop_up {
    __typename
  }
  fragment LINK_ACTION_ACCESSIBILITY_WIDGET on kontent_item_link_action_accessibility_widget {
    __typename
  }
`;

export default Link;
